<template>
  <div class="settings-block">
    <div class="settings-block__title">
      <div class="settings-block__title-text">{{ t('Tracking domains root') }}</div>
      <div class="settings-block__title-buttons">
        <ui-button
          button-style="outline-accent"
          icon="fas fa-check"
          :text="t('Save')"
          :loading="loading"
          @click="submit"
        />
      </div>
    </div>
    <div>
      {{
        t(
          `By default, all visits to your tracking domain's root URL, without a campaign ID (http://XXX.bemobtrk.com/) will display a 404 page. Certain ad networks require this URL to display content relevant to your offers. To comply with this policy, Bemob can redirect it to a specified URL.`,
        )
      }}
    </div>
    <ui-radio-group class="mt-8" v-model="form.rootDomain" :label="t('Destination')">
      <ui-radio :label="t('Show 404 on the root page')" value="404" />
      <ui-radio :label="t('Redirect root to a URL')" value="redirect" />
    </ui-radio-group>
    <div>
      <div class="ma-1">
        <ui-input
          v-model="form.rootRedirect"
          :label="t('Target URL')"
          :placeholder="t('Enter target URL')"
          name="email"
          :disabled="form.rootDomain !== 'redirect'"
        />
        <span v-if="error" class="url-error">{{
          t('Target URL should be a valid URL')
        }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import { pick } from 'lodash';
import { useStore } from '@/store';
import { $emitter } from '@/services/emitter';
import { useI18n } from '@/composables';
import { putUserDomainDataApiRequest } from '@/services/api/domains-api';

const { t } = useI18n();
const store = useStore();

const form = ref<{
  rootDomain: 'redirect' | '404' | null;
  rootRedirect: string | null;
}>({
  rootDomain: null,
  rootRedirect: null,
});

const loading = ref(false);
const error = ref(false);

const fillForm = (source: Record<string, unknown>) => {
  form.value = {
    ...form.value,
    ...pick(source, ['rootDomain', 'rootRedirect']),
  };
};

const submit = async () => {
  loading.value = true;
  try {
    const res = await putUserDomainDataApiRequest(
      form.value as Parameters<typeof putUserDomainDataApiRequest>[0],
    );
    store.mergeUser(res as unknown as Record<string, unknown>);
    error.value = false;
    $emitter.emit('toast-success', t('Tracking Domain’s Destination has been changed'));
  } catch (submitError: unknown) {
    const message = (submitError as { response?: { data?: { message?: string } } })
      ?.response?.data?.message;
    error.value = message === 'VALIDATION_FAILED';
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fillForm((store.user ?? {}) as Record<string, unknown>);
});

watch(
  () => store.user,
  user => {
    fillForm((user ?? {}) as Record<string, unknown>);
  },
);
</script>

<style lang="scss" scoped>
.url-error {
  color: var(--color-red);
}
</style>
